import React, { useCallback } from 'react';
import { ProformaTemplateLibraryInDrawer } from '@atlassian/jira-proforma-template-library/src/ui/index.tsx';
import { generateFormTemplateKey } from '@atlassian/jira-proforma-utils/src/common/utils/generate-form-template-key/index.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import type { TemplateForm } from '@atlassian/proforma-common-core/form-system-models';
import { useCreateProformaRequestTypeForm } from '../../../services/use-create-proforma-form/index.tsx';
import type { CreatedForm } from './types.tsx';

type Props = {
	onFormCreated: (createdForm: CreatedForm) => void;
};

const ProformaFormTemplateLibraryInternal = ({
	projectId,
	onFormCreated,
}: { projectId: number } & Props) => {
	const { createForm } = useCreateProformaRequestTypeForm(projectId);

	const handleTemplateSelected = useCallback(
		async (templateId: string, template: TemplateForm, templateGroup?: string) => {
			const formTemplateKey = generateFormTemplateKey(templateGroup, template.design.settings.name);
			const createdFormResponse = await createForm({
				templateId,
				template,
				formTemplateKey,
			});

			if (createdFormResponse !== undefined) {
				onFormCreated({
					formId: createdFormResponse.form.id,
					formName: createdFormResponse.form.design.settings.name,
				});
			}
		},
		[createForm, onFormCreated],
	);

	return (
		<ProformaTemplateLibraryInDrawer onTemplateSelected={handleTemplateSelected} noBlankTemplate />
	);
};

export const ProformaFormTemplateLibrary = ({ onFormCreated }: Props) => {
	const { data } = useProjectContext();
	const projectId = data?.projectId ?? undefined;
	return projectId !== undefined ? (
		<ProformaFormTemplateLibraryInternal projectId={projectId} onFormCreated={onFormCreated} />
	) : null;
};
